export type Country = 'INDIA' | 'US' | 'CAN';
export type Currency = 'INR' | 'USD' | 'CAD';

export interface ResidencyEntry {
    startAge: number;
    endAge: number;
    country: Country;
}

export interface UserProfile {
    cognitoSub: string;
    birthYear?: number;
    currentAge: number;
    targetRetirementAge: number;
    baseCurrency: Currency;
    residencyHistory: ResidencyEntry[];
    displayName: string;
    createdAt: string;
    updatedAt: string;
}

export type AccountType =
    | 'NRE'
    | 'NRO'
    | 'EQUITY_IN'
    | 'MF_IN'
    | 'REAL_ESTATE'
    | 'PPF'
    | 'NPS'
    | '401K'
    | 'IRA_ROTH'
    | 'IRA_TRADITIONAL'
    | 'TAXABLE_US'
    | 'REAL_ESTATE_US'
    | 'VEHICLE_US'
    | 'TFSA'
    | 'RRSP'
    | 'TAXABLE_CAN'
    | 'REAL_ESTATE_CAN'
    | 'VEHICLE_CAN';

export interface AssetAccount {
    accountId: string;
    country: Country;
    currency: Currency;
    accountType: AccountType;
    label: string;
    balance: number;
    expectedReturn: number;
    ownership: 'SELF' | 'SPOUSE' | 'JOINT';
    linkedGoalId?: string;
    memberSub: string;
    updatedAt: string;
}

export interface HouseholdMembership {
    householdId: string;
    role: 'OWNER' | 'MEMBER';
    joinedAt: string;
}

export interface HouseholdMetadata {
    name: string;
    createdAt: string;
    ownerSub: string;
}

export interface HouseholdResponse {
    membership: HouseholdMembership;
    metadata: HouseholdMetadata;
}

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
    NRE: 'NRE Account',
    NRO: 'NRO Account',
    EQUITY_IN: 'Indian Equity',
    MF_IN: 'Indian Mutual Funds',
    REAL_ESTATE: 'Real Estate (India)',
    PPF: 'PPF',
    NPS: 'NPS',
    '401K': '401(k)',
    IRA_ROTH: 'Roth IRA',
    IRA_TRADITIONAL: 'Traditional IRA',
    TAXABLE_US: 'US Taxable Brokerage',
    REAL_ESTATE_US: 'Real Estate (US)',
    VEHICLE_US: 'Vehicle (US)',
    TFSA: 'TFSA',
    RRSP: 'RRSP',
    TAXABLE_CAN: 'Canada Taxable Brokerage',
    REAL_ESTATE_CAN: 'Real Estate (Canada)',
    VEHICLE_CAN: 'Vehicle (Canada)',
};

export const COUNTRY_ACCOUNT_TYPES: Record<Country, AccountType[]> = {
    INDIA: ['NRE', 'NRO', 'EQUITY_IN', 'MF_IN', 'REAL_ESTATE', 'PPF', 'NPS'],
    US: ['401K', 'IRA_ROTH', 'IRA_TRADITIONAL', 'TAXABLE_US', 'REAL_ESTATE_US', 'VEHICLE_US'],
    CAN: ['TFSA', 'RRSP', 'TAXABLE_CAN', 'REAL_ESTATE_CAN', 'VEHICLE_CAN'],
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
    INR: '₹',
    USD: '$',
    CAD: 'C$',
};

export const COUNTRY_CURRENCIES: Record<Country, Currency> = {
    INDIA: 'INR',
    US: 'USD',
    CAN: 'CAD',
};

export type CashFlowType = 'INCOME' | 'EXPENSE';
export type CashFlowCategory =
    | 'SALARY'
    | 'RENTAL'
    | 'DIVIDEND'
    | 'PENSION'
    | 'HEALTHCARE_BRIDGE'
    | 'LIVING_COST'
    | 'ONE_TIME_EVENT';

export interface DynamicCashFlow {
    cashFlowId: string;
    type: CashFlowType;
    category: CashFlowCategory;
    country: Country;
    amount: number;
    currency: Currency;
    growthRate: number;
    startAge: number;
    endAge: number;
    memberSub: string;
    updatedAt: string;
}

export const CASHFLOW_CATEGORY_LABELS: Record<CashFlowCategory, string> = {
    SALARY: 'Salary',
    RENTAL: 'Rental Income',
    DIVIDEND: 'Dividends',
    PENSION: 'Pension',
    HEALTHCARE_BRIDGE: 'Healthcare Bridge',
    LIVING_COST: 'Living Costs',
    ONE_TIME_EVENT: 'One-Time Event',
};

export const INCOME_CATEGORIES: CashFlowCategory[] = ['SALARY', 'RENTAL', 'DIVIDEND', 'PENSION'];
export const EXPENSE_CATEGORIES: CashFlowCategory[] = ['HEALTHCARE_BRIDGE', 'LIVING_COST', 'ONE_TIME_EVENT'];

export type GoalCategory = 'REAL_ESTATE' | 'EDUCATION' | 'WEDDING' | 'TRAVEL' | 'VEHICLE' | 'OTHER';

export interface Goal {
    goalId: string;
    label: string;
    targetAmount: number;
    currency: Currency;
    targetAge: number;
    country: Country;
    category: GoalCategory;
    memberSub: string;
    updatedAt: string;
}

export const GOAL_CATEGORY_LABELS: Record<GoalCategory, string> = {
    REAL_ESTATE: 'Real Estate',
    EDUCATION: 'Education',
    WEDDING: 'Wedding',
    TRAVEL: 'Travel',
    VEHICLE: 'Vehicle',
    OTHER: 'Other',
};

export interface TaxAssetBreakdown {
    label: string;
    accountType: string;
    country: Country;
    annualGain: number;
    annualTax: number;
    effectiveRate: number;
    taxType: string;
}

export interface TaxProjectionPoint {
    age: number;
    year: number;
    india: number;
    us: number;
    can: number;
}

export interface IndiaTaxBreakdown {
    nroInterestIncome: number;
    nroTds: number;
    rentalIncome: number;
    rentalTds: number;
    ltcgIncome: number;
    ltcgTax: number;
    totalIndiaTax: number;
    dtaaCreditsAvailable: number;
}

export interface TaxActionItem {
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    category: string;
    title: string;
    description: string;
}

export interface TaxAnalysisResult {
    baseCurrency: Currency;
    currentAge: number;
    currentYearTax: { india: number; us: number; can: number; total: number };
    effectiveTaxRate: number;
    taxByAssetClass: TaxAssetBreakdown[];
    projectedAnnualTax: TaxProjectionPoint[];
    indiaTaxBreakdown: IndiaTaxBreakdown;
    nroTdsWarning: boolean;
    dtaaAlert: boolean;
    pficTrapDetected: boolean;
    pficAccountLabels: string[];
    departureTaxRisk: boolean;
    departureTaxEstimateCAD: number;
    rothConversionOpportunity: boolean;
    actionItems: TaxActionItem[];
}

export interface GoalEvent {
    goalId: string;
    label: string;
    targetAmount: number;
    wealthAtEvent: number;
    funded: boolean;
}

export interface GoalFundingSummary {
    goalId: string;
    label: string;
    category: GoalCategory;
    targetAmount: number;
    targetAge: number;
    fundingRate: number;
    medianWealthAtGoalAge: number;
}

export interface YearlyDataPoint {
    year: number;
    age: number;
    netWorthByCountry: { india: number; us: number; can: number };
    projectedTaxByCountry: { india: number; us: number; can: number };
    withdrawalBreakdown: Record<string, number>;
    isShortfallDetected: boolean;
    goalEvents?: GoalEvent[];
}

export type WithdrawalTaxCategory = 'TAX_FREE' | 'CAPITAL_GAINS' | 'TAX_DEFERRED' | 'HIGH_TAX' | 'ILLIQUID';

export interface DrawdownSequenceEntry {
    accountType: string;
    label: string;
    taxCategory: WithdrawalTaxCategory;
    effectiveTaxRate: number;
    priority: number;
}

export interface WithdrawalSummary {
    totalWithdrawn: number;
    totalTaxPaid: number;
    effectiveTaxRate: number;
    taxSavedVsNaive: number;
    drawdownSequence: DrawdownSequenceEntry[];
}

export interface ProjectionResult {
    successRate: number;
    safeAnnualSpend: number;
    baseCurrency: Currency;
    retirementAge: number;
    startingWealth: number;
    yearlyDataPoints: YearlyDataPoint[];
    goalFundingSummary?: GoalFundingSummary[];
    withdrawalSummary?: WithdrawalSummary;
}
